import { formatRange } from '../../../backend/src/utils/productInvestment';

const CURRENCY_LOCALE: Record<string, string> = {
  EUR: 'nl-NL',
  GBP: 'en-GB',
  USD: 'en-US',
};

export function formatCurrency(amount: number, currency = 'EUR'): string {
  const locale = CURRENCY_LOCALE[currency] ?? 'nl-NL';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
}

/** Van–tot bedrag in hele euro's: "€ 2.300,00 – € 3.450,00", of één bedrag zonder bovenkant. */
export function formatCurrencyRange(min: number, max: number | null | undefined, currency = 'EUR'): string {
  return formatRange(min, max, (amount) => formatCurrency(amount, currency));
}

/** Productbedrag zoals de offerte het toont: bandbreedte, bedrag of de prijstekst. */
export function formatProductPrice(
  price: { minCents: number | null; maxCents?: number | null; priceText?: string | null },
  currency = 'EUR'
): string {
  return price.minCents === null
    ? price.priceText || 'Op aanvraag'
    : formatCurrencyRange(price.minCents / 100, price.maxCents == null ? null : price.maxCents / 100, currency);
}

/**
 * Parseert bedragen uit tabelcellen: platte getallen, nl-NL met komma,
 * US/UK met punt als decimaal, optioneel € £ $.
 */
export function parseCurrencyAmount(raw: string): number {
  const trimmed = raw.replace(/\u00a0/g, ' ').replace(/[€£$\u00a3]/g, '').trim();
  if (!trimmed) return 0;
  const compact = trimmed.replace(/\s/g, '');
  // en-US style: 1,234.56
  if (/^\d{1,3}(,\d{3})*\.\d+$/.test(compact) || /^\d+\.\d+$/.test(compact)) {
    const parts = compact.split('.');
    const looksLikeDotThousands =
      !compact.includes(',') &&
      parts.length > 1 &&
      parts[0].length <= 3 &&
      parts.slice(1).every((p) => p.length === 3);
    if (looksLikeDotThousands) {
      const n = parseFloat(parts.join(''));
      return Number.isFinite(n) ? n : 0;
    }
    const n = parseFloat(compact.replace(/,/g, ''));
    return Number.isFinite(n) ? n : 0;
  }
  if (compact.includes(',')) {
    const cleaned = compact.replace(/\./g, '').replace(',', '.');
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : 0;
  }
  const n = parseFloat(compact);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Herkent of een cel echt een bedrag bevat. `parseCurrencyAmount` is bewust
 * tolerant en geeft bij vrije tekst 0 terug; voor investeringstabellen moeten
 * we het verschil tussen bijvoorbeeld `0` en `Op maat` kunnen bewaren.
 */
export function isCurrencyAmount(raw: string): boolean {
  const compact = raw
    .replace(/\u00a0/g, ' ')
    .replace(/[€£$\u00a3]/g, '')
    .replace(/\s/g, '')
    .trim();
  if (!compact) return false;

  return /^-?(?:\d+|\d{1,3}(?:[.,]\d{3})+)(?:[.,]\d{1,2})?$/.test(compact);
}

/** @deprecated Gebruik parseCurrencyAmount — alias voor compatibiliteit. */
export function parseEuroAmount(raw: string): number {
  return parseCurrencyAmount(raw);
}
